import { useTranslation } from 'react-i18next'
import classNames from 'classnames'

import {
  EMPTY_FUNCTION,
  FLOATING_ELEMENT_PLACEMENT,
} from '../../../../constants/common'
import { getQaSelector } from '../../../../utils/common'
import Button, { BUTTON_HEIGHT_24 } from '../../../Button'
import { FloatingSelectDropdown } from '../../../Floating'
import { ICON_HEIGHT_16 } from '../../../Icon'
import { SelectDropdown } from '../../../Select'
import { GretaAIChatMessageRole } from '../../GretaAIChat.constants'
import { GretaAIChatFeedbackForm } from '../../GretaAIChatFeedbackForm'
import { GretaAIChatCopyButton } from '../GretaAIChatCopyButton'
import {
  QA_NAME_GRETA_AI_CHAT_MESSAGE_ACTIONS,
  QA_NAME_GRETA_AI_CHAT_MESSAGE_FEEDBACK_DOWN,
  QA_NAME_GRETA_AI_CHAT_MESSAGE_FEEDBACK_UP,
} from '../GretaAIChatMessageActions.constants'
import { getGretaAIChatFeedbackTooltip } from '../GretaAIChatMessageActions.utils'
import { GretaAIChatMessageTime } from '../GretaAIChatMessageTime'

import { useGretaAIChatAssistantFeedback } from './GretaAIChatAssistantActions.hooks'

import * as styles from './GretaAIChatAssistantActions.module.css'

import DislikeIcon from '../../../../icons/dislike.svg'
import LikeIcon from '../../../../icons/like.svg'

function GretaAIChatAssistantActions({
  className = styles.actions,
  contentRef,
  createdAt,
  feedback,
  isPendingId,
  now,
  text,
  onSubmitFeedback = EMPTY_FUNCTION,
}) {
  const { t } = useTranslation()

  const {
    comment,
    context,
    floatingStyles,
    getFloatingProps,
    handleCancelFeedback,
    handleFeedbackDown,
    handleFeedbackUp,
    handleSubmitFeedback,
    isDown,
    isOpen,
    isUp,
    pendingValue,
    reason,
    refs,
    setComment,
    setReason,
  } = useGretaAIChatAssistantFeedback({ feedback, onSubmitFeedback })

  const { setReference } = refs

  // Pending server id: the PUT would target a message the backend doesn't
  // know yet. Submitting: a resubmit before the first PUT settles could let a
  // stale rejection roll back a newer, already-confirmed vote.
  const isFeedbackDisabled = isPendingId || feedback?.isSubmitting

  const qaProps = getQaSelector({
    context: GretaAIChatMessageRole.Assistant,
    value: QA_NAME_GRETA_AI_CHAT_MESSAGE_ACTIONS,
  })

  return (
    <div {...qaProps} className={className}>
      <GretaAIChatCopyButton
        className={styles.actionButton}
        contentRef={contentRef}
        text={text}
      />

      <span className={styles.feedback} ref={setReference}>
        <Button.Icon
          isGrey
          Icon={LikeIcon}
          beforeIconProps={{ height: ICON_HEIGHT_16 }}
          className={classNames(styles.actionButton, {
            [styles.actionButtonActive]: isUp,
          })}
          disabled={isFeedbackDisabled}
          height={BUTTON_HEIGHT_24}
          qaName={QA_NAME_GRETA_AI_CHAT_MESSAGE_FEEDBACK_UP}
          tooltip={getGretaAIChatFeedbackTooltip({
            isActive: isUp,
            label: t('Greta.FeedbackHelpfulKey'),
            pendingValue,
            t,
          })}
          tooltipPlacement={FLOATING_ELEMENT_PLACEMENT.top}
          onClick={handleFeedbackUp}
        />

        <Button.Icon
          isGrey
          Icon={DislikeIcon}
          beforeIconProps={{ height: ICON_HEIGHT_16 }}
          className={classNames(styles.actionButton, {
            [styles.actionButtonActive]: isDown,
          })}
          disabled={isFeedbackDisabled}
          height={BUTTON_HEIGHT_24}
          qaName={QA_NAME_GRETA_AI_CHAT_MESSAGE_FEEDBACK_DOWN}
          tooltip={getGretaAIChatFeedbackTooltip({
            isActive: isDown,
            label: t('Greta.FeedbackNotHelpfulKey'),
            pendingValue,
            t,
          })}
          tooltipPlacement={FLOATING_ELEMENT_PLACEMENT.top}
          onClick={handleFeedbackDown}
        />
      </span>

      <FloatingSelectDropdown
        context={context}
        floatingStyles={floatingStyles}
        getFloatingProps={getFloatingProps}
        isOpen={isOpen}
        refs={refs}
      >
        <SelectDropdown className={styles.feedbackDropdown}>
          <GretaAIChatFeedbackForm
            comment={comment}
            reason={reason}
            value={pendingValue}
            onCancel={handleCancelFeedback}
            onChangeComment={setComment}
            onChangeReason={setReason}
            onSubmit={handleSubmitFeedback}
          />
        </SelectDropdown>
      </FloatingSelectDropdown>

      <GretaAIChatMessageTime createdAt={createdAt} now={now} />
    </div>
  )
}

export default GretaAIChatAssistantActions
